import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import type { ChartDatum, ChartProps } from "@bfl-ui-toolkit/chart";

const CHART_ANIMATION_DURATION_MS = 500;

const DEFAULT_PRIMARY_COLOR = "#7086FD";
const DEFAULT_SECONDARY_COLOR = "#6FD195";
const DEFAULT_PREFIX = "₹";

const formatValue = (value: number, valuePrefix: string): string => {
  const roundedValue = Math.round(value);
  return `${valuePrefix}${roundedValue.toLocaleString("en-IN")}`;
};

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    <span className="h-2 w-2 shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
    <span className="text-black-normal text-[12px] md:text-[14px]">{label}</span>
  </div>
);

const ChartTooltip: React.FC<
  TooltipProps<number, string> & {
    valuePrefix: string;
    primaryColor: string;
    secondaryColor: string;
  }
> = ({ active, payload, valuePrefix, primaryColor, secondaryColor }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const pointPayload = payload[0]?.payload as ChartDatum | undefined;
  const primaryValue = Number(pointPayload?.primary ?? 0);
  const secondaryValue = Number(pointPayload?.secondary ?? 0);

  return (
    <div
      className="min-w-[123px] rounded-md px-[8px] py-[8px]"
      style={{ backgroundColor: "#F2F2F2" }}
    >
      <div className="mb-1 flex items-center gap-2">
        <span
          className="h-[14px] w-[14px] shrink-0 rounded-full"
          style={{ backgroundColor: secondaryColor }}
        />
        <span className="text-[14px] font-medium text-[#1A1A1A]">
          {formatValue(secondaryValue, valuePrefix)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="h-[14px] w-[14px] shrink-0 rounded-full"
          style={{ backgroundColor: primaryColor }}
        />
        <span className="text-[14px] font-normal text-[#1A1A1A]">
          {formatValue(primaryValue, valuePrefix)}
        </span>
      </div>
    </div>
  );
};

/** SIP area chart with Recharts animation for smooth valid/invalid input transitions. */
export const MonthlySipAreaChart: React.FC<ChartProps> = ({
  data,
  title,
  primaryLabel = "Invested Amount",
  secondaryLabel = "Estimated Returns",
  height = 264,
  valuePrefix = DEFAULT_PREFIX,
  primaryColor = DEFAULT_PRIMARY_COLOR,
  secondaryColor = DEFAULT_SECONDARY_COLOR,
  xAxisFormatter,
  className = "",
  ...props
}) => {
  const safeData = data ?? [];

  return (
    <div
      className={`bfl-chart flex flex-col gap-4 md:flex-row md:gap-6 ${className}`.trim()}
      data-cmp="MonthlySipAreaChart"
      {...props}
    >
      <div className="min-w-0 flex-1">
        {title ? (
          <h3 className="text-black-normal text-[14px] font-medium md:text-[16px]">{title}</h3>
        ) : null}

        <div className="mt-[10px] w-full" style={{ height }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={height}>
            <AreaChart data={safeData} margin={{ left: 20, right: 20, top: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--color-grey-cloud)" vertical horizontal={false} />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={{ strokeWidth: 0.5 }}
                tick={{ fill: "var(--color-black-normal)", fontSize: 12, fontWeight: 400 }}
                interval={0}
                tickFormatter={(value) => (xAxisFormatter ? xAxisFormatter(value) : String(value))}
              />
              <YAxis hide domain={[0, "auto"]} />
              <Tooltip
                cursor={{ stroke: "var(--color-grey-medium)", strokeDasharray: "3 3" }}
                content={
                  <ChartTooltip
                    valuePrefix={valuePrefix}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="primary"
                name={primaryLabel}
                stackId="growth"
                stroke={primaryColor}
                fill={primaryColor}
                fillOpacity={0.5}
                strokeWidth={1.5}
                isAnimationActive
                animationDuration={CHART_ANIMATION_DURATION_MS}
                animationEasing="ease-in-out"
              />
              <Area
                type="monotone"
                dataKey="secondary"
                name={secondaryLabel}
                stackId="growth"
                stroke={secondaryColor}
                fill={secondaryColor}
                fillOpacity={0.55}
                strokeWidth={1.5}
                isAnimationActive
                animationDuration={CHART_ANIMATION_DURATION_MS}
                animationEasing="ease-in-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex w-full flex-row gap-3 md:w-[160px] md:flex-col md:items-start md:justify-center md:gap-4">
        <LegendItem color={secondaryColor} label={secondaryLabel} />
        <LegendItem color={primaryColor} label={primaryLabel} />
      </div>
    </div>
  );
};

MonthlySipAreaChart.displayName = "MonthlySipAreaChart";
